import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

type Transaction = Database['public']['Tables']['transactions']['Row'];
type TransactionType = 'deposit' | 'withdrawal' | 'bonus';

// Тип для статуса withdrawal
export type WithdrawalStatus = 'frozen' | 'completed' | 'cancelled';

// Тип для статуса платежа
export type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'canceled';

// Расширенный тип транзакции с учетом всех полей
export interface EnhancedTransaction extends Omit<Transaction, 'type'> {
  type: TransactionType;
  amount: number;
  comment: string | null;
  created_at: string;
  payment_status: PaymentStatus;
  payment_id: string | null;
  package_id: string | null;
  withdrawal_status: WithdrawalStatus | null;
  record_id: string | null;
}

interface UseTransactionsOptions {
  userId?: string;
  limit?: number;
  offset?: number;
  orderBy?: {
    column: keyof Transaction;
    ascending?: boolean;
  };
  type?: TransactionType;
  recordId?: string;
  withdrawalStatus?: WithdrawalStatus;
  paymentStatus?: PaymentStatus;
  packageId?: string;
}

export const useTransactions = ({
  userId,
  limit = 10,
  offset = 0,
  orderBy = { column: 'created_at', ascending: false },
  type,
  recordId,
  withdrawalStatus,
  paymentStatus,
  packageId
}: UseTransactionsOptions = {}) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['transactions', userId, limit, offset, orderBy, type, recordId, withdrawalStatus, paymentStatus, packageId],
    queryFn: async () => {
      if (!userId) return { transactions: [], count: 0 };

      // Базовый запрос для подсчета общего количества транзакций
      let countQuery = supabase
        .from('transactions')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId);

      // Базовый запрос для получения списка транзакций
      let transactionsQuery = supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId);

      // Фильтрация по типу транзакции
      if (type) {
        countQuery = countQuery.eq('type', type);
        transactionsQuery = transactionsQuery.eq('type', type);
      }

      // Фильтрация по record_id (для транзакций типа withdrawal)
      if (recordId) {
        countQuery = countQuery.eq('record_id', recordId);
        transactionsQuery = transactionsQuery.eq('record_id', recordId);
      }

      // Фильтрация по withdrawal_status (для транзакций типа withdrawal)
      if (withdrawalStatus) {
        countQuery = countQuery.eq('withdrawal_status', withdrawalStatus);
        transactionsQuery = transactionsQuery.eq('withdrawal_status', withdrawalStatus);
      }

      // Фильтрация по payment_status (для транзакций типа deposit)
      if (paymentStatus) {
        countQuery = countQuery.eq('payment_status', paymentStatus);
        transactionsQuery = transactionsQuery.eq('payment_status', paymentStatus);
      }

      // Фильтрация по package_id (для транзакций типа deposit)
      if (packageId) {
        countQuery = countQuery.eq('package_id', packageId);
        transactionsQuery = transactionsQuery.eq('package_id', packageId);
      }

      // Выполняем запрос для подсчета
      const countResult = await countQuery;

      // Добавляем сортировку и пагинацию к запросу транзакций
      const { data: transactions, error } = await transactionsQuery
        .order(orderBy.column, { ascending: orderBy.ascending })
        .range(offset, offset + limit - 1);

      if (error) throw error;

      return {
        transactions: transactions || [],
        count: countResult.count || 0
      };
    },
    enabled: !!userId
  });

  return {
    transactions: data?.transactions as EnhancedTransaction[] || [],
    totalCount: data?.count || 0,
    isLoading,
    error
  };
};

